/**
 * Global Search Component
 *
 * A platform-wide search component with keyboard shortcuts, debouncing,
 * results dropdown, and responsive design. Integrates into the platform header.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { eventBus, Events } from '../services/events';

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  category: string;
  source: string;
  source_name: string;
  url: string;
  icon?: string;
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
  query: string;
}

interface GroupedResults {
  source: string;
  source_name: string;
  results: SearchResult[];
}

@customElement('moya-global-search')
export class MoyaGlobalSearch extends LitElement {
  static styles = css`
    :host {
      display: inline-flex;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .search-container {
      position: relative;
      display: flex;
      align-items: center;
    }

    .search-input-wrapper {
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      padding: 0 12px;
      height: 36px;
      transition: all 0.2s ease;
      min-width: 200px;
      max-width: 400px;
    }

    .search-input-wrapper:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.15);
    }

    .search-input-wrapper:focus-within {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(59, 130, 246, 0.5);
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
    }

    .search-icon {
      width: 16px;
      height: 16px;
      color: rgba(255, 255, 255, 0.5);
      flex-shrink: 0;
    }

    .search-input {
      flex: 1;
      border: none;
      background: transparent;
      color: white;
      font-size: 13px;
      padding: 0 8px;
      outline: none;
      min-width: 0;
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .shortcut-hint {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 2px 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.4);
      font-family: ui-monospace, monospace;
      flex-shrink: 0;
    }

    .clear-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      cursor: pointer;
      padding: 0;
      color: rgba(255, 255, 255, 0.6);
      transition: all 0.15s ease;
      flex-shrink: 0;
    }

    .clear-button:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }

    .clear-button:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .clear-button svg {
      width: 12px;
      height: 12px;
    }

    /* Results dropdown */
    .results-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      min-width: 360px;
      max-height: 400px;
      overflow-y: auto;
      background: #1e293b;
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 10px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
      z-index: 10000;
      padding: 4px;
    }

    .results-group-label {
      padding: 8px 12px 4px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: rgba(255, 255, 255, 0.4);
    }

    .result-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.1s ease;
      text-decoration: none;
      color: inherit;
    }

    .result-item:hover,
    .result-item.active {
      background: rgba(255, 255, 255, 0.08);
    }

    .result-icon {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      background: rgba(59, 130, 246, 0.15);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 14px;
    }

    .result-icon.crm { background: rgba(16, 185, 129, 0.15); color: #10b981; }
    .result-icon.tickets { background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
    .result-icon.default { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }

    .result-icon svg {
      width: 16px;
      height: 16px;
    }

    .result-content {
      flex: 1;
      min-width: 0;
    }

    .result-title {
      font-size: 13px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .result-subtitle {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.4);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 1px;
    }

    .result-badge {
      font-size: 10px;
      font-weight: 600;
      padding: 2px 6px;
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.5);
      flex-shrink: 0;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    .results-empty {
      padding: 24px 16px;
      text-align: center;
      color: rgba(255, 255, 255, 0.4);
      font-size: 13px;
    }

    .results-loading {
      padding: 20px 16px;
      text-align: center;
      color: rgba(255, 255, 255, 0.4);
      font-size: 13px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-top-color: rgba(59, 130, 246, 0.6);
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .results-footer {
      padding: 6px 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.06);
      font-size: 11px;
      color: rgba(255, 255, 255, 0.3);
      text-align: center;
    }

    .results-footer kbd {
      display: inline-block;
      padding: 1px 4px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 3px;
      font-family: ui-monospace, monospace;
      font-size: 10px;
    }

    /* Mobile: Icon only mode */
    .mobile-trigger {
      display: none;
      width: 36px;
      height: 36px;
      border: none;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 8px;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.7);
      transition: all 0.15s ease;
      align-items: center;
      justify-content: center;
    }

    .mobile-trigger:hover {
      background: rgba(255, 255, 255, 0.12);
      color: white;
    }

    .mobile-trigger:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .mobile-trigger svg {
      width: 18px;
      height: 18px;
    }

    /* Mobile expanded overlay */
    .mobile-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9998;
    }

    .mobile-overlay.visible {
      display: block;
    }

    .mobile-search-panel {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #1e293b;
      padding: 12px;
      z-index: 9999;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }

    .mobile-search-panel.visible {
      display: block;
    }

    .mobile-search-panel .search-input-wrapper {
      width: 100%;
      max-width: none;
    }

    .mobile-close {
      position: absolute;
      right: 12px;
      top: 12px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      border-radius: 4px;
    }

    .mobile-close:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    .mobile-close:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    @media (max-width: 640px) {
      .desktop-search {
        display: none;
      }

      .mobile-trigger {
        display: flex;
      }

      .results-dropdown {
        min-width: unset;
      }
    }

    /* CSS Variables for theming */
    :host {
      --search-bg: rgba(255, 255, 255, 0.08);
      --search-bg-hover: rgba(255, 255, 255, 0.12);
      --search-bg-focus: rgba(255, 255, 255, 0.15);
      --search-border: rgba(255, 255, 255, 0.1);
      --search-border-focus: rgba(59, 130, 246, 0.5);
      --search-text: white;
      --search-placeholder: rgba(255, 255, 255, 0.4);
    }
  `;

  @property({ type: String }) placeholder = 'Search...';
  @property({ type: String }) searchUrl = '';
  @property({ type: String, attribute: 'search-api-url' }) searchApiUrl = '';
  @property({ type: String }) token = '';
  @property({ type: Number }) debounceMs = 300;
  @property({ type: Boolean }) showShortcutHint = true;

  @state() private _query = '';
  @state() private mobileExpanded = false;
  @state() private results: SearchResult[] = [];
  @state() private isLoading = false;
  @state() private showResults = false;
  @state() private activeIndex = -1;
  @state() private hasSearched = false;

  @query('.search-input') private searchInput!: HTMLInputElement;
  @query('.mobile-input') private mobileInput!: HTMLInputElement;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private keyboardHandler: ((e: KeyboardEvent) => void) | null = null;
  private clickOutsideHandler: ((e: MouseEvent) => void) | null = null;
  private abortController: AbortController | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.keyboardHandler = this.handleGlobalKeyboard.bind(this);
    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener('keydown', this.keyboardHandler);
    document.addEventListener('click', this.clickOutsideHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  private handleClickOutside(e: MouseEvent) {
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.showResults = false;
    }
  }

  private handleGlobalKeyboard(e: KeyboardEvent) {
    // Cmd/Ctrl + K to focus search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      this.focusSearch();
    }

    // Escape to close results or mobile
    if (e.key === 'Escape') {
      if (this.showResults) {
        this.showResults = false;
        return;
      }
      if (this.mobileExpanded) {
        this.closeMobileSearch();
      }
    }
  }

  private focusSearch() {
    // Check if mobile view
    if (window.innerWidth <= 640) {
      this.mobileExpanded = true;
      // Wait for render, then focus
      requestAnimationFrame(() => {
        this.mobileInput?.focus();
      });
    } else {
      this.searchInput?.focus();
    }
  }

  private handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this._query = input.value;
    this.activeIndex = -1;

    // Debounce the search
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (!this._query.trim()) {
      this.results = [];
      this.showResults = false;
      this.hasSearched = false;
      this.isLoading = false;
      this.emitSearchQuery();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.emitSearchQuery();
      this.fetchResults();
    }, this.debounceMs);
  }

  private handleInputKeydown(e: KeyboardEvent) {
    if (!this.showResults || this.results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, -1);
    } else if (e.key === 'Enter' && this.activeIndex >= 0) {
      e.preventDefault();
      this.navigateToResult(this.results[this.activeIndex]);
    }
  }

  private async fetchResults() {
    if (!this.searchApiUrl || !this._query.trim()) return;

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.isLoading = true;
    this.showResults = true;

    try {
      const url = new URL(this.searchApiUrl);
      url.searchParams.set('q', this._query.trim());
      url.searchParams.set('limit', '8');

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const response = await fetch(url.toString(), {
        headers,
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        this.results = [];
        this.hasSearched = true;
        return;
      }

      const data: SearchResponse = await response.json();
      this.results = data.results || [];
      this.hasSearched = true;
      this.activeIndex = -1;
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        this.results = [];
        this.hasSearched = true;
      }
    } finally {
      this.isLoading = false;
    }
  }

  private getGroupedResults(): GroupedResults[] {
    const groups = new Map<string, GroupedResults>();
    for (const result of this.results) {
      if (!groups.has(result.source)) {
        groups.set(result.source, {
          source: result.source,
          source_name: result.source_name,
          results: [],
        });
      }
      groups.get(result.source)!.results.push(result);
    }
    return Array.from(groups.values());
  }

  private navigateToResult(result: SearchResult) {
    this.showResults = false;

    this.dispatchEvent(
      new CustomEvent('moya-search-navigate', {
        bubbles: true,
        composed: true,
        detail: { result },
      })
    );

    if (result.url) {
      window.location.href = result.url;
    }
  }

  private getResultGlobalIndex(result: SearchResult): number {
    return this.results.indexOf(result);
  }

  private emitSearchQuery() {
    const detail = { query: this._query, timestamp: Date.now() };

    // Emit DOM event
    this.dispatchEvent(
      new CustomEvent('moya-search', {
        bubbles: true,
        composed: true,
        detail,
      })
    );

    // Emit to EventBus
    eventBus.emit(Events.SEARCH_QUERY, detail);
  }

  private handleSubmit(e: Event) {
    e.preventDefault();

    // If a result is selected, navigate to it
    if (this.activeIndex >= 0 && this.results[this.activeIndex]) {
      this.navigateToResult(this.results[this.activeIndex]);
      return;
    }

    // Clear any pending debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    const detail = { query: this._query };

    // Emit submit event
    this.dispatchEvent(
      new CustomEvent('moya-search-submit', {
        bubbles: true,
        composed: true,
        detail,
      })
    );

    eventBus.emit(Events.SEARCH_SUBMIT, detail);

    // Navigate to search URL if no API (fallback)
    if (this.searchUrl && this._query && !this.searchApiUrl) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set('q', this._query);
      window.location.href = url.toString();
    }

    // Close mobile if open
    if (this.mobileExpanded) {
      this.closeMobileSearch();
    }
  }

  private handleClear() {
    this._query = '';
    this.results = [];
    this.showResults = false;
    this.hasSearched = false;
    this.activeIndex = -1;

    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Emit clear event
    this.dispatchEvent(
      new CustomEvent('moya-search-clear', {
        bubbles: true,
        composed: true,
      })
    );

    eventBus.emit(Events.SEARCH_CLEAR, {});

    // Re-focus input
    if (this.mobileExpanded) {
      this.mobileInput?.focus();
    } else {
      this.searchInput?.focus();
    }
  }

  private openMobileSearch() {
    this.mobileExpanded = true;
    requestAnimationFrame(() => {
      this.mobileInput?.focus();
    });
  }

  private closeMobileSearch() {
    this.mobileExpanded = false;
    this.showResults = false;
  }

  private getShortcutKey(): string {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return isMac ? '⌘K' : 'Ctrl+K';
  }

  private getSourceIcon(source: string) {
    switch (source) {
      case 'crm':
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
      case 'tickets':
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/></svg>`;
      default:
        return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" stroke-linecap="round"/></svg>`;
    }
  }

  /** Stable id for the listbox of a given variant (referenced by aria-controls). */
  private listboxId(variant: string): string {
    return `moya-search-results-${variant}`;
  }

  /** Stable id for an individual option (referenced by aria-activedescendant). */
  private optionId(variant: string, globalIdx: number): string {
    return `moya-search-result-${variant}-${globalIdx}`;
  }

  private renderResultsDropdown(variant: 'desktop' | 'mobile') {
    if (!this.showResults) return nothing;

    const listboxId = this.listboxId(variant);

    if (this.isLoading) {
      return html`
        <div class="results-dropdown" id="${listboxId}" role="listbox" aria-label="Search results">
          <div class="results-loading" role="status" aria-live="polite">
            <div class="spinner" aria-hidden="true"></div>
            Searching...
          </div>
        </div>
      `;
    }

    if (this.hasSearched && this.results.length === 0) {
      return html`
        <div class="results-dropdown" id="${listboxId}" role="listbox" aria-label="Search results">
          <div class="results-empty" role="status" aria-live="polite">
            No results found for "${this._query}"
          </div>
        </div>
      `;
    }

    if (this.results.length === 0) return nothing;

    const groups = this.getGroupedResults();

    return html`
      <div class="results-dropdown" id="${listboxId}" role="listbox" aria-label="Search results">
        ${groups.map(
          (group) => html`
            <div class="results-group-label" role="presentation">${group.source_name} (${group.results.length})</div>
            ${group.results.map((result) => {
              const globalIdx = this.getResultGlobalIndex(result);
              return html`
                <div
                  class="result-item ${classMap({ active: globalIdx === this.activeIndex })}"
                  @click="${() => this.navigateToResult(result)}"
                  @mouseenter="${() => { this.activeIndex = globalIdx; }}"
                  role="option"
                  id="${this.optionId(variant, globalIdx)}"
                  aria-selected="${globalIdx === this.activeIndex ? 'true' : 'false'}"
                  aria-label="${result.title}${result.subtitle ? ' — ' + result.subtitle : ''} (${result.category})"
                >
                  <div class="result-icon ${result.source}" aria-hidden="true">
                    ${this.getSourceIcon(result.source)}
                  </div>
                  <div class="result-content">
                    <div class="result-title">${result.title}</div>
                    ${result.subtitle ? html`<div class="result-subtitle">${result.subtitle}</div>` : nothing}
                  </div>
                  <span class="result-badge">${result.category}</span>
                </div>
              `;
            })}
          `
        )}
        <div class="results-footer" aria-hidden="true">
          <kbd>↑↓</kbd> navigate &middot; <kbd>↵</kbd> open &middot; <kbd>esc</kbd> close
        </div>
      </div>
    `;
  }

  private renderSearchInput(inputClass: string, variant: 'desktop' | 'mobile') {
    const activeId = this.activeIndex >= 0 ? this.optionId(variant, this.activeIndex) : '';
    return html`
      <form @submit="${this.handleSubmit}" role="search" aria-label="Platform search (${variant})">
        <div class="search-input-wrapper">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" stroke-linecap="round" />
          </svg>
          <input
            type="text"
            class="search-input ${inputClass}"
            placeholder="${this.placeholder}"
            .value="${this._query}"
            @input="${this.handleInput}"
            @keydown="${this.handleInputKeydown}"
            @focus="${() => { if (this._query.trim() && this.hasSearched) this.showResults = true; }}"
            aria-label="Search across the platform"
            aria-autocomplete="list"
            aria-expanded="${this.showResults ? 'true' : 'false'}"
            aria-controls="${this.listboxId(variant)}"
            aria-activedescendant="${activeId}"
            autocomplete="off"
          />
          ${this._query
            ? html`
                <button type="button" class="clear-button" @click="${this.handleClear}" aria-label="Clear search">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" />
                  </svg>
                </button>
              `
            : this.showShortcutHint
              ? html`<span class="shortcut-hint" aria-hidden="true">${this.getShortcutKey()}</span>`
              : ''}
        </div>
      </form>
    `;
  }

  render() {
    const overlayClasses = classMap({
      'mobile-overlay': true,
      visible: this.mobileExpanded,
    });

    const panelClasses = classMap({
      'mobile-search-panel': true,
      visible: this.mobileExpanded,
    });

    return html`
      <div class="search-container">
        <!-- Desktop search -->
        <div class="desktop-search">
          ${this.renderSearchInput('desktop-input', 'desktop')}
          ${this.renderResultsDropdown('desktop')}
        </div>

        <!-- Mobile trigger -->
        <button class="mobile-trigger" @click="${this.openMobileSearch}" aria-label="Open search" aria-haspopup="dialog" aria-expanded="${this.mobileExpanded ? 'true' : 'false'}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" stroke-linecap="round" />
          </svg>
        </button>

        <!-- Mobile overlay -->
        <div class="${overlayClasses}" @click="${this.closeMobileSearch}" aria-hidden="true"></div>

        <!-- Mobile search panel -->
        <div class="${panelClasses}" role="${this.mobileExpanded ? 'dialog' : ''}" aria-label="Search" aria-modal="${this.mobileExpanded ? 'true' : 'false'}">
          ${this.mobileExpanded ? this.renderSearchInput('mobile-input', 'mobile') : nothing}
          ${this.mobileExpanded ? this.renderResultsDropdown('mobile') : nothing}
          <button class="mobile-close" @click="${this.closeMobileSearch}" aria-label="Close search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" />
            </svg>
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-global-search': MoyaGlobalSearch;
  }
}
